import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

// A single drawer item renders a header row with a fixed select button and a down arrow.
// Clicking the header toggles expansion to reveal extra content.
const DrawerItem = ({
  item,
  index,
  isExpanded,
  toggle,
  onSelect,
  renderItemContent,
  selectButtonText,
}) => {
  return (
    <div className="border rounded">
      <div
        className="flex justify-between items-center p-2 cursor-pointer hover:bg-gray-100"
        onClick={() => toggle(index)}
      >
        <span className="font-bold text-sm">
          {item.label || `Section ${item.section.toUpperCase()}`}
        </span>
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item);
            }}
            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {selectButtonText || "Select"}
          </button>
          <svg
            className={`w-4 h-4 transform transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            }`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {isExpanded && (
        <div className="p-2 border-t">
          {renderItemContent ? renderItemContent(item) : null}
        </div>
      )}
    </div>
  );
};

const SectionsDrawer = ({
  isOpen,
  onClose,
  title,
  items = [],
  onSelectItem,
  renderItemContent,
  selectButtonText,
  footer,
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const toggleItem = (index) => {
    setExpandedItems((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  if (!shouldRender) return null;

  return createPortal(
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[1000] bg-black transition-opacity duration-300 ease-in-out ${
          isOpen ? "opacity-50" : "opacity-0"
        }`}
      ></div>
      <div
        id="drawer-right"
        className={`fixed top-0 right-0 z-[1010] h-screen p-4 overflow-y-auto overflow-x-hidden transition-transform duration-300 ease-in-out bg-white w-[500px] dark:bg-gray-800 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        tabIndex="-1"
        aria-labelledby="drawer-right-label"
      >
        <div className="flex items-center justify-between mb-4">
          <h5 id="drawer-right-label" className="text-base font-semibold text-gray-500 dark:text-gray-400">
            {title || "Other Sections"}
          </h5>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-900 rounded-lg text-sm w-6 h-6 inline-flex items-center justify-center"
          >
            <svg
              className="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7L1 13"
              />
            </svg>
            <span className="sr-only">Close menu</span>
          </button>
        </div>
        <div className="space-y-2">
          {items.map((item, i) => (
            <DrawerItem
              key={i}
              item={item}
              index={i}
              isExpanded={expandedItems[i]}
              toggle={toggleItem}
              onSelect={onSelectItem}
              renderItemContent={renderItemContent}
              selectButtonText={selectButtonText}
            />
          ))}
        </div>
        {footer && <div className="mt-4">{footer}</div>}
      </div>
    </>,
    document.body
  );
};

export default SectionsDrawer;
